import { World } from "./World";
import { Tile, TileType } from "./Tile";
import { GameObj } from "./GameObj";
import { InfluenceMap } from "./InfluenceMap";
import { Faction } from "./types";
import { StatsData } from "./Stats";

interface Resources {
    blood: number;
    souls: number;
    runes: number;
}

interface GameObjData {
    id: string;
    faction: Faction;
    unitId: string;
    cooldownStart: number;
    x: number;
    y: number;
}

interface TileData {
    x: number;
    y: number;
    img: string;
    type: TileType;
}

interface WorldData {
    tiles: TileData[];
    objs: GameObjData[];
}

interface InfluenceData {
    x: number;
    y: number;
    influence: Faction;
}

export interface SaveData {
    version: number;
    neutralUnitMaxAmount: number;
    difficulty: number;
    currentTutorialStep: number;
    camX: number;
    camY: number;
    zoom: number;
    isOverworld: boolean;
    factionData: { player: Resources; green: Resources; blue: Resources; neutral: Resources };
    overworld: WorldData;
    netherworld: WorldData;
    influence: InfluenceData[];
    aiTracking: { player: string[]; green: string[]; blue: string[]; neutral: string[] };
    stats: StatsData;
}

export class SaveManager {

    static readonly KEY = "nethereign_game";
    static readonly VERSION = 1;
    static readonly MAX_FAILURES = 10;

    private static failures = 0;

    static hasSave(): boolean {
        return SaveManager.read() !== null;
    }

    static clear(): void {
        try {
            localStorage.removeItem(SaveManager.KEY);
        } catch (e) {
        }
    }

    static read(): SaveData | null {
        try {
            const raw = localStorage.getItem(SaveManager.KEY);
            if (!raw) return null;
            const data = JSON.parse(raw) as SaveData;
            if (!data || data.version !== SaveManager.VERSION) return null;
            return data;
        } catch (e) {
            return null;
        }
    }

    static write(data: SaveData): void {
        if (SaveManager.failures >= SaveManager.MAX_FAILURES) return;
        try {
            localStorage.setItem(SaveManager.KEY, JSON.stringify(data));
        } catch (e) {
            SaveManager.failures++;
        }
    }

    static serializeWorld(world: World): WorldData {
        const tiles: TileData[] = [];
        for (const { x, y, tile } of world.tilesIterator()) {
            tiles.push({ x, y, img: tile.img, type: tile.type });
        }
        const objs: GameObjData[] = [];
        for (const { x, y, obj } of world) {
            objs.push({ x, y, id: obj.id, faction: obj.faction, unitId: obj.unitId, cooldownStart: obj.getCooldownStart() });
        }
        return { tiles, objs };
    }

    static serializeInfluence(map: InfluenceMap): InfluenceData[] {
        const out: InfluenceData[] = [];
        for (const { x, y, influence } of map) {
            out.push({ x, y, influence });
        }
        return out;
    }

    static buildWorld(data: WorldData, idMap: Map<string, GameObj>): World {
        const world = new World(0, false, null, 0);
        world.setTiles(data.tiles.map(t => ({ x: t.x, y: t.y, tile: new Tile(t.img, t.type) })));
        for (const o of data.objs) {
            const obj = new GameObj(o.faction, o.unitId, o.id);
            obj.setCooldownStart(o.cooldownStart);
            world.set(o.x, o.y, obj);
            idMap.set(o.id, obj);
        }
        return world;
    }

    static applyInfluence(map: InfluenceMap, data: InfluenceData[]): void {
        for (const e of data) {
            map.set(e.x, e.y, e.influence);
        }
    }

    static syncIdCounter(idMap: Map<string, GameObj>): void {
        let max = 0;
        for (const id of idMap.keys()) {
            const n = parseInt(id, 10);
            if (!isNaN(n)) max = Math.max(max, n);
        }
        GameObj.setIdCounter(max + 1);
    }
}
